import React, { useContext, useMemo } from 'react';

// DEPENDENCIES
import { useSelector } from 'react-redux';

// GLOBAL VARIABLES
import { DELIVERY_LOG_TYPE } from 'codes.js';
import { ENDPOINTS } from 'endpoints';
import { SORT_ORDER } from 'globals.js';
import { INTERFACE } from 'pathnames.js';

// LOCAL VARIABLES
import { ACTIONS, COLUMNS } from '../../../../columns.js';

// CORE COMPONENTS
import Form from 'core/form/Form';

// GLOBAL COMPONENTS
import Main from 'parts/main/Main';
import Table, { Filters } from 'parts/table/Table.js';
import TableSticky from 'parts/table/tools/TableSticky.js';
import Title from 'parts/title/Title';
import ExcelButtons from 'routers/interface/tools/ExcelButtons';

// LOCAL COMPONENTS
import OverviewHeader from './parts/OverviewHeader.js';
import OverviewTable from './parts/OverviewTable.js';
import OverviewToolbar from './parts/OverviewToolbar.js';
import StatusCell from './parts/StatusCell.js';

// CODES
import { DELIVERY_LOG_STATUS } from 'codes.js';

// HELPERS
import { getListData, ListContext } from 'helpers/getListData';

// MAIN COMPONENT
const Overview = () => {
  // CONTEXT
  const { result } = useContext(ListContext) || {};

  // REDUX
  const isMobile = useSelector(({ mobile }) => mobile.mobile);

  // MEMOS
  const sortedResult = useMemo(
    () =>
      result
        ?.map(({ delivered_date, overview_sale_records, ...rest }) => ({
          title: delivered_date,
          data: (overview_sale_records || []).map(r => ({ ...r, ...rest }))
        }))
        .filter(table => !!table.data.length),
    [result]
  );

  // RENDER
  return (
    <Main>
      <Title
        documentTitle="Delivery Log -> OverView-"
        title="Delivery Log Overview"
        tools={<ExcelButtons fileName="delivery_log_overview" />}
      />
      <Filters>
        <Filters.Date.Range />
        <Filters.Button.SalesType
          allOption={{
            key: 'total_records'
          }}
        />
        <Filters.Button.NewUsed
          allOption={{
            key: 'total_records'
          }}
        />
        <Filters.Sort label="Sort By Date" />
      </Filters>
      {isMobile && (
        <>
          <OverviewHeader />
          <OverviewTable />
        </>
      )}
      <Table.Sorted
        sortedResult={sortedResult}
        toolbar={OverviewToolbar}
        mobilePreviewKeys={['delivered_date', 'customer1', 'vehicle']}
        autoSizeHeight={!isMobile}
        tableBefore={
          <TableSticky>
            <OverviewHeader />
            <OverviewTable />
          </TableSticky>
        }
      />
    </Main>
  );
};

// EXPORT
export default getListData(Overview, {
  key: 'deliveryLogOverview',
  fetchArgs: {
    endpoint: ENDPOINTS.deliveryLog.list,
    params: {
      deliveryLogType: DELIVERY_LOG_TYPE.OverView.code
    }
  },
  defaults: {
    sort: 'delivered_date',
    order: SORT_ORDER.ascending,
    startDate: Form.Date.OPTIONS.thisMonth.startDate,
    endDate: Form.Date.OPTIONS.thisMonth.endDate,
    startRow: null
  },
  columns: [
    {
      ...COLUMNS.deliveredDate,
      label: 'Time',
      tooltip: 'Time when deal was entered into the system.',
      width: 70,
      component: undefined
    },
    {
      component: StatusCell,
      key: 'sales_status_code',
      icon: 'info',
      tooltip: 'Status',
      codes: DELIVERY_LOG_STATUS,
      headerClassName: 'justify-content-center',
      width: 50
    },
    {
      ...COLUMNS.salesPerson,
      key: 'salesperson1',
      key2: 'salesperson2'
    },
    {
      ...COLUMNS.customer(),
      key: 'customer1',
      key2: 'customer2'
    },
    {
      ...COLUMNS.ageOfVehicle,
      key: 'age'
    },
    COLUMNS.newUsed,
    COLUMNS.saleSubtype,
    COLUMNS.purchaseLease,
    {
      ...COLUMNS.retailWholesale,
      key: 'sale_type_category_code'
    },
    COLUMNS.stockNumber,
    {
      ...COLUMNS.makeModel,
      key: 'vehicle'
    },
    {
      ...COLUMNS.trades,
      key: 'aryTrade'
    },
    {
      ...COLUMNS.frontendGross,
      key: 'fe_gross_bp'
    },
    COLUMNS.amGross,
    COLUMNS.fiGross,
    {
      ...COLUMNS.totalGrossBP,
      key: 'total_fe_be_gross_bp'
    },
    COLUMNS.dealerPack,
    {
      ...COLUMNS.totalGrossAP,
      key: 'total_fe_be_gross_ap'
    }
  ],
  actions: [ACTIONS.open(INTERFACE.sales, 'sales_id')]
});
